// Client-side PostHog singleton. Server-side capture lives in posthog.server.js;
// this file handles the browser SDK (posthog-js) — the only place that can see
// the anonymous, pre-signup top of the funnel (homepage, search) and stitch it
// to a user. Identify happens HERE, not on the server: the backend SDK has no
// concept of an anonymous session, so a server-side identify only updates a
// profile and can never merge the anonymous events. See
// .claude/skills/integration-javascript_web/references/identify-users.md.
//
// posthog-js is about 100 KB gzipped, so it loads beside hydration instead of
// ahead of it: initPostHog() starts the import and returns, and the app
// hydrates without waiting. Calls made before the SDK arrives (identify on
// the first render, request_form_viewed, paywall_viewed) queue and replay in
// order once it initializes; captures keep the time they happened. What the
// SDK can't see before it loads are its own automatic events: clicks
// autocaptured in that window, and the pageview of a page the visitor has
// already navigated away from.

let posthog = null; // the SDK, once initialized
let queue = null; // calls waiting for it; null when PostHog is off

export function initPostHog() {
  if (posthog || queue || typeof window === "undefined") return;
  const env = window.env || {};
  // POSTHOG_PUBLIC_KEY is the public project token (safe in the browser), exposed
  // via the root loader's `env` object — same channel as SENTRY_DSN.
  if (!env.POSTHOG_PUBLIC_KEY || env.APP_ENV === "test") return;

  queue = [];
  import("posthog-js")
    .then(({ default: sdk }) => {
      sdk.init(env.POSTHOG_PUBLIC_KEY, {
        // Browser events go through the first-party managed reverse proxy
        // (POSTHOG_PROXY_HOST, e.g. https://j.schnerp.com) so ad blockers — which
        // blocklist *.i.posthog.com — don't silently drop them; falls back to the
        // direct ingestion host when the proxy var is unset (e.g. local dev). The
        // server SDK (posthog.server.js) stays on POSTHOG_HOST — server events
        // aren't ad-blocked and needn't add proxy load.
        api_host: env.POSTHOG_PROXY_HOST || env.POSTHOG_HOST,
        // Required when api_host is a proxy: keeps the PostHog app domain for the
        // toolbar and session-recording player links (they'd break otherwise).
        ui_host: "https://us.posthog.com",
        // Anonymous events are cheaper and still merge into the person on identify.
        person_profiles: "identified_only",
        // Capture $pageview on initial load AND Remix client-side navigations.
        capture_pageview: "history_change",
        capture_pageleave: true,
      });
      posthog = sdk;
      for (const call of queue) call(sdk);
      queue = null;
    })
    .catch(() => {
      // The SDK didn't load (offline, a failed chunk): analytics stays off
      // for this page view rather than queueing forever.
      queue = null;
    });
}

function withPostHog(call) {
  if (posthog) call(posthog);
  else if (queue) queue.push(call);
}

// Call once the user is known (any auth method). Merges the current anonymous
// distinct_id into the identified person so pre-signup events stay attached.
export function identifyPostHogUser(user) {
  if (!user?.id) return;
  const distinctId = String(user.id);
  withPostHog((sdk) => {
    if (sdk.get_distinct_id() === distinctId) return;
    sdk.identify(distinctId, { email: user.email });
  });
}

// Call on logout so the next visitor on a shared device starts fresh. The app
// calls this on every logged-out page load, so it resets only an identified
// person: resetting an anonymous visitor gave each full page load a new
// anonymous id, splitting one visitor into several and leaving identify only
// the last page's events to merge.
export function resetPostHog() {
  withPostHog((sdk) => {
    if (sdk.get_property("$user_state") === "identified") sdk.reset();
  });
}

// Client-side custom events (no-op on the server and when PostHog is off). An
// event queued while the SDK loads is sent with the time it happened.
export function capturePostHog(event, properties) {
  const timestamp = new Date();
  withPostHog((sdk) => sdk.capture(event, properties, { timestamp }));
}
